import React, { useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import { toast } from 'react-toastify'
import { DELETE_TAG, GET_TAGS, RENAME_TAG } from '../../api/tag'
import { Tag } from '../../types'
import VhsDialog from '../Common/VhsDialog'

type Change = { renamed?: Tag; deletedId?: number }

type Props = {
  open: boolean
  onClose: () => void
  onChanged?: (change: Change) => void
}

// Lists the tags the current user may rename or delete: every tag for site
// admins and National Sec Maal, otherwise the tags they created
// (voucher-tags.md §4). The server enforces the same rule.
const ManageTagsDialog = ({ open, onClose, onChanged }: Props) => {
  const tagsQuery = useQuery(GET_TAGS, {
    skip: !open,
    fetchPolicy: 'cache-and-network',
  })
  const [renameTag] = useMutation(RENAME_TAG)
  const [deleteTag] = useMutation(DELETE_TAG)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [draftName, setDraftName] = useState('')
  const [tagToDelete, setTagToDelete] = useState<Tag | null>(null)

  const manageable: Tag[] = (tagsQuery.data?.tags ?? [])
    .filter((t: Tag) => t.can_manage)
    .sort((a: Tag, b: Tag) => a.name.localeCompare(b.name))

  const saveRename = async (tag: Tag) => {
    const name = draftName.trim()
    if (!name || name === tag.name) {
      setEditingId(null)
      return
    }
    try {
      const { data } = await renameTag({ variables: { id: tag.id, name } })
      setEditingId(null)
      tagsQuery.refetch()
      onChanged?.({ renamed: data.renameTag })
      toast.success(`Renamed to “${data.renameTag.name}”`)
    } catch (err: any) {
      toast.error(err.message || 'Failed to rename tag')
    }
  }

  const confirmDelete = async () => {
    if (!tagToDelete) return
    try {
      await deleteTag({ variables: { id: tagToDelete.id } })
      tagsQuery.refetch()
      onChanged?.({ deletedId: tagToDelete.id })
      toast.success(`Deleted tag “${tagToDelete.name}”`)
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete tag')
    } finally {
      setTagToDelete(null)
    }
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Manage tags</DialogTitle>
        <DialogContent dividers>
          {manageable.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {tagsQuery.loading
                ? 'Loading…'
                : 'There are no tags you can rename or delete. You can manage the tags you created.'}
            </Typography>
          ) : (
            <List dense disablePadding>
              {manageable.map((tag) => (
                <ListItem
                  key={tag.id}
                  disableGutters
                  secondaryAction={
                    editingId === tag.id ? (
                      <Box>
                        <IconButton
                          size="small"
                          aria-label="Save"
                          onClick={() => saveRename(tag)}
                        >
                          <CheckIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label="Cancel"
                          onClick={() => setEditingId(null)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      <Box>
                        <Tooltip title="Rename">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setEditingId(tag.id)
                              setDraftName(tag.name)
                            }}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setTagToDelete(tag)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )
                  }
                  sx={{ pr: 10 }}
                >
                  {editingId === tag.id ? (
                    <TextField
                      size="small"
                      autoFocus
                      fullWidth
                      value={draftName}
                      inputProps={{ maxLength: 40 }}
                      onChange={(e) => setDraftName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveRename(tag)
                        if (e.key === 'Escape') setEditingId(null)
                      }}
                    />
                  ) : (
                    <Typography variant="body2">
                      {tag.name}
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 1 }}
                      >
                        {tag.voucher_count === 1
                          ? '1 voucher'
                          : `${tag.voucher_count ?? 0} vouchers`}
                      </Typography>
                    </Typography>
                  )}
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="inherit">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <VhsDialog
        open={!!tagToDelete}
        onClose={() => setTagToDelete(null)}
        onConfirm={confirmDelete}
        title="Delete tag?"
        warningMessage={
          tagToDelete
            ? // No count here: voucher_count only covers vouchers this user
              // can see, but deleting removes the tag everywhere.
              `“${tagToDelete.name}” will be removed from every voucher that carries it. The vouchers themselves are not affected.`
            : undefined
        }
        confirmButtonText="Delete"
        color="error"
        showReasonField={false}
      />
    </>
  )
}

export default ManageTagsDialog
